import { Component, type ErrorInfo, type ReactNode } from 'react';
import { formatUserMessage } from '@ebu/contracts';
import type { BrowserLogger } from '../diagnostics/browser-logger';

interface Props {
  logger: BrowserLogger;
  children: ReactNode;
}
interface State {
  failed: boolean;
}

/** Top-level boundary: logs WEB-RUNTIME-001 and offers a concrete recovery action with the code. */
export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    const firstComponent = info.componentStack?.trim().split('\n')[0]?.trim() ?? 'unknown';
    this.props.logger.error('web-shell', 'Render error caught by application boundary', {
      code: 'WEB-RUNTIME-001',
      origin: 'apps/web/src/app/AppErrorBoundary.tsx:componentDidCatch',
      context: { errorName: error.name, detail: error.message, component: firstComponent },
    });
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <main className="page" id="main">
        <div className="notice notice--error" role="alert">
          <h1>This screen stopped working</h1>
          <p>{formatUserMessage('WEB-RUNTIME-001')}</p>
          <div className="button-row">
            <button type="button" className="button" onClick={() => window.location.reload()}>
              Reload page
            </button>
            <a className="button button--secondary" href="/">
              Go to start
            </a>
          </div>
        </div>
      </main>
    );
  }
}
